"use client";
import React, { useRef, useState } from "react";
import styles from "./Listbox.module.scss";
import Link from "next/link";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import { ClickOutside } from "@aleksundshantu/aunds-utils-react/accessibility";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG.client";

interface ListProps {
  items: { label: string; value: string }[];
  selected: string;
  setSelected: (value: string) => void;
  disableDropdown?: boolean;
  availableTranslations: { lang: string; path: string }[];
  alwaysOpenUntil?: "navbar-breakpoint" | "always";
  closeOnSelection?: boolean;
}

export const List = ({
  items,
  selected: _selected,
  setSelected: _setSelected,
  disableDropdown,
  availableTranslations,
  alwaysOpenUntil,
  closeOnSelection,
}: ListProps) => {
  //state that is only used internally if no external state is provided
  const [internalSelected, setInternalSelected] = useState<string | undefined>(
    undefined,
  );
  const selected = _selected ?? internalSelected;
  const setSelected = _setSelected ?? setInternalSelected;

  const [open, setOpen] = useState(false);

  const ref = useRef<HTMLUListElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  const { getLabel, getIcon } = useGlobalContext();

  return (
    <div
      data-is-open={open}
      className={`${styles.listbox} dropdown ${open ? "is-active" : ""} ${alwaysOpenUntil ? styles[`always-open-until-${alwaysOpenUntil}`] : ""}`}
    >
      <div className={`dropdown-trigger ${disableDropdown ? "disabled" : ""}`}>
        <button
          className="button is-flex is-align-items-center is-gap-1"
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          onClick={() => {
            setOpen(!open);
          }}
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              setOpen(!open);
            }
            if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          ref={button}
        >
          <span className="navbar-link selected-lang">
            {selected
              ? items.find((i) => i.value === selected)?.label
              : "Auswählen..."}
          </span>
          {!disableDropdown && (
            <div className={styles["arrow-wrapper"]}>
              <CustomSVG
                src={getIcon("chevron-down")}
                wrapper={"span"}
                width={12}
                height={12}
                alt="chevron-down"
                className="is-flex"
              />
            </div>
          )}
        </button>
      </div>
      {!disableDropdown && (
        <div className="dropdown-menu" id="dropdown-menu">
          <ClickOutside
            elementToExclude={button}
            onClickOutside={() => setOpen(false)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- ARIA APG: focus must move into dropdown on open */}
            <FocusTrap dep={open} disabled={!open} autoFocus>
              <ul
                role="menu"
                className="dropdown-content m-0"
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    ref.current?.focus();
                  }
                }}
                aria-label={getLabel("aria_label_language_toggle")}
              >
                {items.map((item, index) => {
                  const translatedPath = availableTranslations.find(
                    (x) => x.lang === item.value,
                  );

                  return (
                    <li className="dropdown-item m-0" key={index} role="none">
                      {
                        // eslint-disable-next-line react/forbid-elements
                        <Link
                          role="menuitem"
                          href={translatedPath?.path ?? ""}
                          locale={item.value}
                          aria-label={
                            getLabel(
                              "aria_label_language_toggle_option_" + item.value,
                            ) || undefined
                          }
                          className={` navbar-link ${selected === item.value ? "is-active" : ""}`}
                          onClick={() => {
                            if (closeOnSelection) setOpen(false);
                            setSelected(item.value);
                          }}
                          lang={item.value}
                          hrefLang={item.value}
                        >
                          {item.label}
                        </Link>
                      }
                    </li>
                  );
                })}
              </ul>
            </FocusTrap>
          </ClickOutside>
        </div>
      )}
    </div>
  );
};
/* jscpd:ignore-start */
type ListPropsV2Base = {
  buttonId?: string;
  items: { label: string; value: string }[];
  disableDropdown?: boolean;
  availableTranslations?: { lang: string; path: string }[];
  alwaysOpenUntil?: "navbar-breakpoint";
  className?: string;
  placeholder?: string;
};

type ListPropsV2NumbersMode = ListPropsV2Base & ListPropsV2NumbersModeValues;

export type ListPropsV2NumbersModeValues = {
  numbersForOption: true;
  selected: Record<string, number> | undefined;
  setSelected: (value: Record<string, number>) => void;
};

type ListPropsV2StringMode = ListPropsV2Base & ListPropsV2StringModeValues;

export type ListPropsV2StringModeValues = {
  numbersForOption?: false;
  selected: string | undefined;
  setSelected: (value: string) => void;
};

export type ListPropsV2 = ListPropsV2NumbersMode | ListPropsV2StringMode;

// one guard on the whole props object — no need for twin guards anymore,
// since selected and setSelected are tied together by the same discriminant
const isNumbersMode = (props: ListPropsV2): props is ListPropsV2NumbersMode =>
  props.numbersForOption === true;

export const ListV2 = (props: ListPropsV2) => {
  const {
    items,
    disableDropdown,
    availableTranslations,
    alwaysOpenUntil,
    buttonId,
    className = "",
    placeholder = "Auswählen...",
    numbersForOption,
  } = props;
  //state that is only used internally if no external state is provided
  const [open, setOpen] = useState(false);

  const ref = useRef<HTMLUListElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const { getIcon } = useGlobalContext();

  return (
    <div
      data-is-open={open}
      className={`${styles["listbox"]} ${className} dropdown ${open ? "is-active" : ""} ${
        alwaysOpenUntil ? styles[`always-open-until-${alwaysOpenUntil}`] : ""
      }`}
    >
      <div
        className={`dropdown-trigger w-full ${
          disableDropdown ? "disabled" : ""
        }`}
      >
        <button
          type={"button"} // important! otherwise forms with listbox inside will be submitted
          id={buttonId}
          className="button is-flex is-align-items-center is-justify-content-space-between is-gap-1 has-text-left w-full"
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          onClick={() => {
            setOpen(!open);
          }}
          onKeyUp={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          ref={button}
        >
          <span
            className={
              availableTranslations
                ? "navbar-link selected-lang"
                : `placeholder ${props.selected ? "is-selected" : ""}`
            }
          >
            {isNumbersMode(props)
              ? items
                  .map((i) => i.label + ": " + (props.selected?.[i.value] ?? 0))
                  .join(", ")
              : props.selected
                ? items.find((i) => i.value === props.selected)?.label
                : placeholder}
          </span>
          {!disableDropdown && (
            <div className={styles["arrow-wrapper"]}>
              <CustomSVG
                src={getIcon("chevron-down")}
                wrapper={"span"}
                width={12}
                height={12}
                alt="chevron-down"
                className="is-flex"
              />
            </div>
          )}
        </button>
      </div>
      {!disableDropdown && (
        <div
          className="dropdown-menu w-full"
          id="dropdown-menu"
          data-lenis-prevent
        >
          <ClickOutside
            elementToExclude={button}
            onClickOutside={() => setOpen(false)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- ARIA APG: focus must move into listbox on open */}
            <FocusTrap dep={open} disabled={!open} autoFocus>
              <ul
                role="listbox"
                className="dropdown-content m-0"
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    ref.current?.focus();
                  }
                }}
              >
                {items.map((item, index) => {
                  const translatedPath = availableTranslations?.find(
                    (x) => x.lang === item.value,
                  );

                  if (translatedPath) {
                    if (isNumbersMode(props)) {
                      return;
                    }
                    return (
                      <li className="dropdown-item m-0" key={index}>
                        {
                          // eslint-disable-next-line react/forbid-elements
                          <Link
                            role="menuitem"
                            href={translatedPath?.path ?? ""}
                            locale={item.value}
                            className={` navbar-link ${
                              props.selected === item.value ? "is-active" : ""
                            }`}
                            onClick={() => props.setSelected(item.value)}
                            lang={item.value}
                            hrefLang={item.value}
                          >
                            {item.label}
                          </Link>
                        }
                      </li>
                    );
                  }

                  const currentValue = isNumbersMode(props)
                    ? (props.selected?.[item.value] ?? 0)
                    : 0;

                  return (
                    <li
                      className={`dropdown-item m-0 ${numbersForOption ? "is-flex is-justify-content-space-between is-align-items-center" : ""}`}
                      key={index}
                      role="option"
                      aria-selected={props.selected === item.value}
                      onKeyDown={(
                        event: React.KeyboardEvent<HTMLLIElement>,
                      ) => {
                        if (event.key === "Enter") {
                          if (isNumbersMode(props)) {
                            return;
                          }
                          setOpen(false);
                          props.setSelected(item.value);
                        }
                      }}
                      onClick={() => {
                        if (isNumbersMode(props)) {
                          return;
                        }
                        setOpen(false);
                        props.setSelected(item.value);
                      }}
                    >
                      {item.label}
                      {isNumbersMode(props) && (
                        <div className="is-flex is-gap-1 is-align-items-center">
                          <button
                            className={`button-reset ${styles["number-change-button"]}`}
                            type="button"
                            disabled={currentValue <= 0}
                            onClick={() =>
                              props.setSelected({
                                [item.value]: currentValue - 1,
                              })
                            }
                          >
                            -
                          </button>
                          <span className={styles["number-current-value"]}>
                            {currentValue}
                          </span>
                          <button
                            className={`button-reset ${styles["number-change-button"]}`}
                            type="button"
                            onClick={() =>
                              props.setSelected({
                                [item.value]: currentValue + 1,
                              })
                            }
                          >
                            +
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </FocusTrap>
          </ClickOutside>
        </div>
      )}
    </div>
  );
};
/* jscpd:ignore-end */
